import { archivedInquiriesSchema, clientExportPageSchema, clientExportRowSchema, clientExportSelectionSchema, } from '@ghp/domain';
import { bearer } from './http.js';
import { queryString } from './query-string.js';
/**
 * L'export client (D26, M26) : lire ce qui est dû, et livrer.
 *
 * Le jeton voyage **en argument**, comme partout ailleurs : celui de la session en cours, et rien
 * d'autre.
 */
export function createExportsEndpoints(http) {
    return {
        /**
         * La file des contrôles validés — filtrée, paginée et **comptée par le serveur** (D24).
         *
         * ⚠️ Le chemin porte `inquiries` parce que c'est ce que la file contient : des demandes
         * validées, pas des exports passés. `Inspection` n'existe pas encore (M26).
         */
        list(accessToken, request = {}) {
            return http.get(`/exports/client/inquiries${queryString(request)}`, clientExportPageSchema, bearer(accessToken));
        },
        /**
         * « Tout exporter » : les identifiants de **tout** l'ensemble filtré — les mêmes filtres que
         * `list`, sans les bornes de page —, au plus 500. ⚠️ **`truncated` levé : la sélection est
         * coupée**, et l'écran doit le dire avant d'exporter.
         */
        selectAll(accessToken, request = {}) {
            return http.get(`/exports/client/inquiries/ids${queryString(request)}`, clientExportSelectionSchema, bearer(accessToken));
        },
        /**
         * Le **dossier complet** d'un contrôle en `.zip` — rapport, photos, schéma, pièces. ⚠️ C'est
         * une **livraison** : la ligne passe « livrée » (M26), comme après `download`. `409` avant la
         * validation.
         */
        bundle(accessToken, inquiryId) {
            return http.getFile(`/exports/client/inquiries/${inquiryId}/bundle`, bearer(accessToken));
        },
        /**
         * Le **classeur « Rapport AC Terrain »** de GHP d'un contrôle, rempli (#420). Une livraison,
         * comme le dossier : elle laisse sa trace.
         */
        workbook(accessToken, inquiryId) {
            return http.getFile(`/exports/client/inquiries/${inquiryId}/workbook`, bearer(accessToken));
        },
        /**
         * **Plusieurs contrôles en une archive** (#420) : un dossier par référence GHP, classeur et
         * rapport, et le CSV récapitulatif. Une livraison par contrôle.
         */
        folders(accessToken, inquiryIds) {
            return http.postFile('/exports/client/folders', { inquiryIds }, bearer(accessToken));
        },
        /** Les **photos** seules, en `.zip` — sans trace de livraison : c'est une consultation. */
        photos(accessToken, inquiryId) {
            return http.getFile(`/exports/client/inquiries/${inquiryId}/photos`, bearer(accessToken));
        },
        /**
         * Livre la sélection : le serveur compose le fichier **et consigne la livraison** (D26).
         *
         * ⚠️ **Un appel, pas deux.** Télécharger puis marquer comme exporté laisserait la seconde
         * moitié échouer seule, et la file dirait « à livrer » d'un fichier déjà chez le client.
         *
         * ⚠️ **Une liste d'identifiants, jamais un filtre** (M14 § 5) : ce que l'utilisateur a coché
         * est ce sur quoi il agit. Rejouer le filtre côté serveur livrerait les lignes arrivées entre
         * l'affichage et le clic.
         */
        /**
         * « Saisie terminée » : la saisie du rapport d'un contrôle validé est faite. Rend la ligne à
         * jour. ⚠️ **Idempotent** — un second appel garde la première date ; `409` hors d'un contrôle
         * validé.
         */
        markEntryDone(accessToken, inquiryId) {
            return http.postJson(`/exports/client/inquiries/${inquiryId}/entry-done`, {}, clientExportRowSchema, bearer(accessToken));
        },
        download(accessToken, inquiryIds) {
            return http.postFile('/exports/client', { inquiryIds }, bearer(accessToken));
        },
        /**
         * « Valider & archiver » — la **dernière marche du cycle de vie** (M3,
         * `validated → archived`), que rien n'écrivait : l'écran « Archives » lisait un statut que
         * personne ne posait.
         *
         * ⚠️ **Un geste à part de la livraison**, comme au prototype : on livre souvent avant
         * d'avoir la confirmation du client.
         *
         * ⚠️ **Tout ou rien** : une sélection dont une ligne n'est plus archivable est refusée en
         * entier, en nommant les fautives. Archiver la moitié sans le dire laisserait croire le lot
         * clos.
         */
        archive(accessToken, inquiryIds) {
            return http.postJson('/exports/client/archive', { inquiryIds }, archivedInquiriesSchema, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=exports.js.map